import { default as WJElement, event } from '../wje-element/element.js';
import styles from './styles/styles.css?inline';
import SlidingContainerBackdrop from './sliding-container.backdrop.js';
import {
    clampDrawerSize,
    resolveDrawerDirection,
    resolveDrawerHeight,
    resolveDrawerScope,
    resolveDrawerVariant,
} from './sliding-container.presentation.js';
import { createSlidingContainerView } from './sliding-container.view.js';
import SlidingContainerTransition from './sliding-container.transition.js';

/**
 * @summary SlidingContainer is a custom web component that extends WJElement.
 * @documentation https://elements.webjet.sk/components/SlidingContainer
 * @status stable
 * @augments WJElement
 * @csspart backdrop - Provides the style source for the viewport backdrop.
 * @csspart sliding-container - Styles the native sliding panel.
 * @csspart expanded - Styles the optional expanded panel.
 * @csspart close-button - Styles the generated close button.
 * @csspart sheet-handle-area - Styles the draggable resize handle hit area.
 * @csspart sheet-handle - Styles the visible draggable resize handle.
 * @slot - The default slot for the SlidingContainer.
 * @slot expanded - Optional content rendered next to the base panel while expanded.
 * @property {string} maxWidth - The maximum width of the SlidingContainer.
 * @property {string} maxHeight - The maximum height of the SlidingContainer.
 * @property {string} trigger - The trigger for the SlidingContainer.
 * @property {string|null} expandedWidth - Width added by the expanded panel.
 * @property {string} expandedDirection - Physical side where the expanded panel is rendered.
 * @property {string|null} expandedTrigger - Optional document event that toggles expansion.
 * @property {'left'|'right'|'bottom'} direction - Edge from which the drawer opens.
 * @property {string} variant - Determines how the SlidingContainer behaves, such as 'over' or 'in-place'.
 * @property {string} screenBreakPoint - The width (in pixels) at which the SlidingContainer switches to the "over" variant for smaller screens.
 * @property {boolean} removeChildAfterClose - Removes the child after the SlidingContainer is closed.
 * @property {string} animationDuration - Specifies the duration (in milliseconds) of the sliding animation.
 * @property {string} animationEasing - Specifies the easing function used for the sliding animation (e.g., 'linear', 'ease-in', 'ease-out').
 * @property {boolean} hasOpacity - Sets the opacity of the SlidingContainer.
 * @property {string} mobilePresentation - Legacy responsive adapter. Prefer direction="bottom".
 * @property {string} mobileBreakPoint - Legacy viewport width where mobile presentation becomes active.
 * @property {boolean} backdrop - Shows a viewport backdrop while the container is open. Legacy bottom-sheet usage with backdrop-dismiss keeps its implicit backdrop.
 * @property {boolean} backdropDismiss - Closes the container when its backdrop is clicked.
 * @property {string|null} scope - Frame used by a bottom drawer: "container", "parent", or "viewport".
 * @property {boolean} sheetResizable - Enables bottom drawer resizing.
 * @property {string} sheetScope - Legacy alias that defines the bottom sheet scope.
 * @property {string} sheetBoundary - CSS selector for the composed ancestor that bounds the mobile bottom sheet panel.
 * @property {string} sheetHeight - The bottom drawer height after opening.
 * @property {string} sheetMinHeight - The minimum bottom drawer height while resizing.
 * @property {string} sheetMaxHeight - The maximum bottom drawer height while resizing.
 * @attribute {string} mobile-presentation - Legacy responsive adapter. Prefer direction="bottom".
 * @attribute {string} expanded-width - Width of the optional expanded panel.
 * @attribute {string} expanded-direction - Physical side where the expanded panel is rendered: "left" or "right".
 * @attribute {string} expanded-trigger - Document event that toggles the expanded panel.
 * @attribute {'left'|'right'|'bottom'} direction - Edge from which the drawer opens. Defaults to "right".
 * @attribute {string} mobile-break-point - Viewport width where mobile presentation becomes active. Defaults to "768".
 * @attribute {boolean} backdrop - Shows a viewport backdrop for every direction.
 * @attribute {boolean} backdrop-dismiss - Closes the container when its visible backdrop is clicked.
 * @attribute {string} scope - Frame used by a bottom drawer: "container", "parent", or "viewport".
 * @attribute {boolean} sheet-resizable - Enables resizing the bottom drawer with a handle.
 * @attribute {string} sheet-scope - Legacy scope alias for bottom-sheet compatibility.
 * @attribute {string} sheet-boundary - CSS selector for the composed ancestor that bounds the mobile bottom sheet panel.
 * @attribute {string} sheet-height - The bottom drawer height after opening.
 * @attribute {string} sheet-min-height - The minimum bottom drawer height while resizing.
 * @attribute {string} sheet-max-height - The maximum bottom drawer height while resizing.
 * @cssproperty [--wje-sliding-container-background=var(--wje-background)] - Background of the sliding panel.
 * @cssproperty [--wje-sliding-container-expanded-background=var(--wje-sliding-container-background)] - Background of the expanded panel.
 * @cssproperty [--wje-sliding-container-z-index=1000] - z-index of the side sliding container.
 * @cssproperty [--wje-sliding-container-bottom-sheet-z-index=var(--wje-sliding-container-z-index, 1000)] - z-index of the bottom drawer host.
 * @cssproperty [--wje-sliding-container-bottom-sheet-panel-z-index=1] - z-index of the bottom drawer panel inside the host layer.
 * @cssproperty [--wje-sliding-container-backdrop-z-index] - z-index of the body-level viewport backdrop. Defaults to one layer below the host.
 * @cssproperty [--wje-sliding-container-portal-backdrop-z-index] - Deprecated fallback for the viewport backdrop z-index.
 * @cssproperty [--wje-sliding-container-backdrop-background=var(--wje-backdrop)] - Background of the viewport backdrop.
 * @cssproperty [--wje-sliding-container-backdrop-opacity=1] - Backdrop opacity while the container is open.
 * @cssproperty [--wje-sliding-container-backdrop-filter=none] - CSS backdrop filter applied to the viewport backdrop.
 * @cssproperty [--wje-sliding-container-box-shadow=var(--wje-sliding-container-shadow)] - Shadow of the bottom drawer panel.
 * @cssproperty [--wje-sliding-container-sheet-border-radius=var(--wje-sliding-container-border-radius) var(--wje-sliding-container-border-radius) 0 0] - Border radius of the bottom drawer panel.
 * @cssproperty [--wje-sliding-container-sheet-handle-area-height=24px] - Height of the resize handle hit area.
 * @cssproperty [--wje-sliding-container-sheet-handle-width=36px] - Width of the visible resize handle.
 * @cssproperty [--wje-sliding-container-sheet-handle-height=2px] - Height of the visible resize handle.
 * @cssproperty [--wje-sliding-container-sheet-handle-background=var(--wje-border-color)] - Background of the visible resize handle.
 * @cssproperty [--wje-sliding-container-sheet-handle-radius=999px] - Border radius of the visible resize handle.
 * @tag wje-sliding-container
 * @example
 * <wje-sliding-container trigger="test-resize-container-event-right" id="left-in-place" direction="left" max-width="100px" max-height="100%">
 *    <wje-card>
 *       <wje-card-header>
 *        <wje-card-subtitle>CONTENT Subtitle</wje-card-subtitle>
 *       <wje-card-title>CONTENT Title</wje-card-title>
 *      </wje-card-header>
 *     <wje-card-content>
 *        CONTENT Lorem ipsum dolor sit amet, consectetur adipiscing elit.
 *    </wje-card-content>
 * </wje-card>
 * </wje-sliding-container>
 */
export default class SlidingContainer extends WJElement {
    /**
     * Creates an instance of SlidingContainer.
     * @class
     */
    constructor() {
        super();

        this._isOpen = false;
        this._isClosing = false;
        this._lastCaller = null;
        this._sheetDrag = null;
        this._sheetIgnoreDismissUntil = 0;
        this._sheetBackdropGesture = null;
        this._sheetBackdropHandledUntil = 0;
        this._bottomScopeFrame = null;
        this._resizeObserver = null;
        this._boundTrigger = null;
        this._boundExpandedTrigger = null;
        this._sideOverStyleSnapshot = null;
        this._isExpanded = false;
        this._expandedTarget = false;
        this._expandedWidthPixels = 0;
        this._expandedTransition = null;
        this._expandedOperationPromise = null;
        this._expandedOperationVersion = 0;
        this.backdropController = new SlidingContainerBackdrop(this);
        this.baseTransition = new SlidingContainerTransition();

        this.startResizeObservation();
    }

    /**
     * Sets the maximum width of an element by updating the 'max-width' attribute.
     * @param {string} value The maximum width value to be set (e.g., '100px', '50%', etc.).
     */
    set maxWidth(value) {
        this.setAttribute('max-width', value);
    }

    /**
     * Gets the maximum width value of the element.
     * Retrieves the value of the 'max-width' attribute. If the attribute is not set, it defaults to 'auto'.
     * @returns {string} The maximum width value of the element or 'auto' if the attribute is not defined.
     */
    get maxWidth() {
        return this.getAttribute('max-width') ?? 'auto';
    }

    /**
     * Sets the maximum height for the element.
     * @param {string} value The maximum height value to be applied to the element. This can include units such as "px", "em", "%", etc.
     */
    set maxHeight(value) {
        this.setAttribute('max-height', value);
    }

    /**
     * Retrieves the maximum height value of the element, or returns 'auto' if not set.
     * @returns {string} The maximum height value or 'auto' if the attribute is not specified.
     */
    get maxHeight() {
        return this.getAttribute('max-height') ?? 'auto';
    }

    /**
     * Sets the 'trigger' attribute for the element.
     * @param {string} value The value to set for the 'trigger' attribute.
     */
    set trigger(value) {
        this.setAttribute('trigger', value);
    }

    /**
     * Retrieves the value of the 'trigger' attribute. If the attribute is not set, it defaults to 'sliding-container'.
     * @returns {string} The value of the 'trigger' attribute or the default value 'sliding-container' if not defined.
     */
    get trigger() {
        return this.getAttribute('trigger') ?? 'sliding-container';
    }

    /**
     * Sets the width of the optional expanded panel.
     * @param {string} value A CSS width value.
     */
    set expandedWidth(value) {
        this.setAttribute('expanded-width', value);
    }

    /**
     * Gets the configured expanded panel width.
     * @returns {string|null} A CSS width value or null when expansion is not configured.
     */
    get expandedWidth() {
        return this.getAttribute('expanded-width');
    }

    /**
     * Sets the physical side where the expanded panel is rendered.
     * @param {string} value Either "left" or "right".
     */
    set expandedDirection(value) {
        this.setAttribute('expanded-direction', value);
    }

    /**
     * Gets the physical side where the expanded panel is rendered. By default,
     * it is opposite to the base sliding direction.
     * @returns {string} Either "left" or "right".
     */
    get expandedDirection() {
        const configuredDirection = this.getAttribute('expanded-direction');
        if (configuredDirection === 'left' || configuredDirection === 'right') {
            return configuredDirection;
        }

        return this.direction === 'right' ? 'left' : 'right';
    }

    /**
     * Sets the document event used to toggle the expanded panel.
     * @param {string} value The document event name.
     */
    set expandedTrigger(value) {
        this.setAttribute('expanded-trigger', value);
    }

    /**
     * Gets the document event used to toggle the expanded panel.
     * @returns {string|null} The event name or null.
     */
    get expandedTrigger() {
        return this.getAttribute('expanded-trigger');
    }

    /**
     * Sets the edge from which the drawer opens.
     * @param {'left'|'right'|'bottom'} value Supported drawer direction.
     */
    set direction(value) {
        this.setAttribute('direction', value);
    }

    /**
     * Retrieves a normalized drawer direction.
     * @returns {'left'|'right'|'bottom'} Configured direction or 'right'.
     */
    get direction() {
        return resolveDrawerDirection({
            direction: this.getAttribute('direction'),
            viewportWidth: Number.POSITIVE_INFINITY,
        });
    }

    /**
     * Gets the direction used by the current responsive presentation. The
     * legacy mobile-presentation API is normalized to "bottom" at this boundary.
     * @returns {'left'|'right'|'bottom'} Effective rendering direction.
     */
    get presentationDirection() {
        return resolveDrawerDirection({
            direction: this.direction,
            mobilePresentation: this.mobilePresentation,
            mobileBreakPoint: this.mobileBreakPoint,
            viewportWidth: window.innerWidth,
        });
    }

    /**
     * Gets the effective layout variant without changing the public attribute.
     * @returns {'in-place'|'over'} Effective variant.
     */
    get presentationVariant() {
        return resolveDrawerVariant({
            direction: this.presentationDirection,
            variant: this.variant,
            screenBreakPoint: this.screenBreakPoint,
            viewportWidth: window.innerWidth,
        });
    }

    /**
     * Gets the frame used by the current bottom presentation. Direct bottom
     * drawers default to their container; the legacy responsive adapter keeps
     * its viewport default.
     * @returns {'container'|'parent'|'viewport'} Effective scope.
     */
    get presentationScope() {
        return resolveDrawerScope({
            scope: this.scope,
            legacyScope: this.getAttribute('sheet-scope'),
            direction: this.getAttribute('direction'),
        });
    }

    /**
     * Sets the value of the `remove-child-after-close` attribute.
     * This attribute determines if a child element should be removed after a close operation.
     * @param {boolean|string} value The value to set for the `remove-child-after-close` attribute. The value can be a boolean or a string representation of a boolean.
     */
    set removeChildAfterClose(value) {
        this.setAttribute('remove-child-after-close', value);
    }

    /**
     * Gets the value indicating whether the child element should be removed after closing.
     *
     * This property checks the presence of the 'remove-child-after-close' attribute on the element.
     * Returns `false` if the attribute does not exist.
     * @returns {boolean} True if the 'remove-child-after-close' attribute is present, otherwise false.
     */
    get removeChildAfterClose() {
        return this.hasAttribute('remove-child-after-close') ?? false;
    }

    /**
     * Sets the 'variant' attribute to the specified value.
     * @param {string} value The value to set for the 'variant' attribute.
     */
    set variant(value) {
        this.setAttribute('variant', value);
    }

    /**
     * Retrieves the value of the "variant" attribute. If the attribute is not set,
     * it returns the default value 'in-place'.
     * @returns {string} The variant value or the default value 'in-place'.
     */
    get variant() {
        return this.getAttribute('variant') ?? 'in-place';
    }

    /**
     * Retrieves the value of the 'screen-break-point' attribute.
     * @returns {string} The value of the 'screen-break-point' attribute.
     */
    get screenBreakPoint() {
        return this.getAttribute('screen-break-point');
    }

    /**
     * Sets the screen break point value to determine responsive behavior.
     * @param {string} value The value to set as the screen break point.
     */
    set screenBreakPoint(value) {
        this.setAttribute('screen-break-point', value);
    }

    /**
     * Sets mobile presentation mode.
     * @param {string} value The mobile presentation mode.
     */
    set mobilePresentation(value) {
        this.setAttribute('mobile-presentation', value);
    }

    /**
     * Gets mobile presentation mode.
     * @returns {string|null} The mobile presentation mode.
     */
    get mobilePresentation() {
        return this.getAttribute('mobile-presentation');
    }

    /**
     * Sets the breakpoint for mobile presentation.
     * @param {string} value The mobile breakpoint.
     */
    set mobileBreakPoint(value) {
        this.setAttribute('mobile-break-point', value);
    }

    /**
     * Gets the breakpoint for mobile presentation.
     * @returns {string} The mobile breakpoint.
     */
    get mobileBreakPoint() {
        return this.getAttribute('mobile-break-point') || '768';
    }

    /**
     * Sets the duration of the animation by updating the `animation-duration` attribute.
     * @param {string} value The duration value for the animation, specified in a format
     * such as seconds (e.g., "2s") or milliseconds (e.g., "200ms").
     */
    set animationDuration(value) {
        this.setAttribute('animation-duration', value);
    }

    /**
     * Gets the animation duration for an element.
     * It retrieves the value of the 'animation-duration' attribute if present; otherwise, it defaults to '500'.
     * @returns {string} The value of the animation duration, either from the attribute or the default '500'.
     */
    get animationDuration() {
        return this.getAttribute('animation-duration') ?? '500';
    }

    /**
     * Sets the easing function for the animation.
     * @param {string} value The easing function to use for the animation. This can be any valid CSS timing function such as "ease", "linear", "ease-in", "ease-out", etc.
     */
    set animationEasing(value) {
        this.setAttribute('animation-easing', value);
    }

    /**
     * Retrieves the easing function for the animation.
     * @returns {string} The value of the 'animation-easing' attribute if set, otherwise defaults to 'linear'.
     */
    get animationEasing() {
        return this.getAttribute('animation-easing') ?? 'linear';
    }

    /**
     * Determines if the element has an 'has-opacity' attribute.
     * @returns {boolean} True if the element has the 'has-opacity' attribute, otherwise false.
     */
    get hasOpacity() {
        return this.hasAttribute('has-opacity') ?? false;
    }

    /**
     * Determines whether a viewport backdrop is shown while the panel is open.
     * The legacy bottom-sheet adapter preserves its historical implicit backdrop
     * when backdrop-dismiss is present.
     * @returns {boolean} True when the backdrop is enabled explicitly or by legacy compatibility.
     */
    get backdrop() {
        return this.hasAttribute('backdrop')
            || (this.backdropDismiss && this.usesLegacyBottomSheetPresentation());
    }

    /**
     * Enables or disables the viewport backdrop.
     * @param {boolean} value Whether the backdrop should be enabled.
     */
    set backdrop(value) {
        this.toggleAttribute('backdrop', Boolean(value));
    }

    /**
     * Determines if the container should close when the backdrop is clicked.
     * @returns {boolean} True if backdrop dismiss is enabled.
     */
    get backdropDismiss() {
        return this.hasAttribute('backdrop-dismiss') ?? false;
    }

    /**
     * Enables resizing for the mobile bottom sheet.
     * @returns {boolean} True if the sheet can be resized.
     */
    get sheetResizable() {
        return this.hasAttribute('sheet-resizable') ?? false;
    }

    /**
     * Sets resizing for the mobile bottom sheet.
     * @param {boolean|string} value The value to set.
     */
    set sheetResizable(value) {
        this.setAttribute('sheet-resizable', value);
    }

    /**
     * Gets the preferred bottom drawer scope.
     * @returns {string|null} Configured scope, or null for the context default.
     */
    get scope() {
        return this.getAttribute('scope');
    }

    /**
     * Sets the preferred bottom drawer scope.
     * @param {string|null} value Either "container", "parent", or "viewport".
     */
    set scope(value) {
        if (value === null || value === undefined) {
            this.removeAttribute('scope');
        } else {
            this.setAttribute('scope', value);
        }
    }

    /**
     * Gets the mobile bottom sheet panel scope. The backdrop always covers the viewport.
     * @returns {string} Either 'viewport', 'container', or 'parent'.
     */
    get sheetScope() {
        return this.getAttribute('sheet-scope') || 'viewport';
    }

    /**
     * Sets the mobile bottom sheet panel scope.
     * @param {string} value The new boundary mode for the mobile sheet.
     */
    set sheetScope(value) {
        this.setAttribute('sheet-scope', value);
    }

    /**
     * Gets the selector used for container-scoped mobile bottom sheet panels.
     * @returns {string|null} A CSS selector used to find the composed boundary ancestor.
     */
    get sheetBoundary() {
        return this.getAttribute('sheet-boundary');
    }

    /**
     * Sets the selector used for container-scoped mobile bottom sheet panels.
     * @param {string} value Selector for the boundary element.
     */
    set sheetBoundary(value) {
        this.setAttribute('sheet-boundary', value);
    }

    /**
     * Gets the mobile bottom sheet opening height.
     * @returns {string|null} The opening height.
     */
    get sheetHeight() {
        return this.getAttribute('sheet-height');
    }

    /**
     * Sets the mobile bottom sheet opening height.
     * @param {string} value The opening height.
     */
    set sheetHeight(value) {
        this.setAttribute('sheet-height', value);
    }

    /**
     * Gets the minimum mobile bottom sheet height while resizing.
     * @returns {string|null} The minimum height.
     */
    get sheetMinHeight() {
        return this.getAttribute('sheet-min-height');
    }

    /**
     * Sets the minimum mobile bottom sheet height while resizing.
     * @param {string} value The minimum height.
     */
    set sheetMinHeight(value) {
        this.setAttribute('sheet-min-height', value);
    }

    /**
     * Gets the maximum mobile bottom sheet height while resizing.
     * @returns {string|null} The maximum height.
     */
    get sheetMaxHeight() {
        return this.getAttribute('sheet-max-height');
    }

    /**
     * Sets the maximum mobile bottom sheet height while resizing.
     * @param {string} value The maximum height.
     */
    set sheetMaxHeight(value) {
        this.setAttribute('sheet-max-height', value);
    }

    /**
     * Sets the value of the 'add-to-height' attribute.
     * This attribute is used to modify or adjust the height dynamically.
     * @param {string} value The value to be assigned to the 'add-to-height' attribute.
     */
    set addToHeight(value) {
        this.setAttribute('add-to-height', value);
    }

    /**
     * Retrieves the value of the 'add-to-height' attribute from the element.
     * If the attribute is not set, it defaults to '0'.
     * @returns {string} The value of the 'add-to-height' attribute or '0' if the attribute is not present.
     */
    get addToHeight() {
        return this.getAttribute('add-to-height') ?? '0';
    }

    /**
     * Determines whether the current state is open.
     * @returns {boolean} True if the state is open, otherwise false.
     */
    get isOpen() {
        return this._isOpen;
    }

    /**
     * Determines whether the optional expanded panel is currently open.
     * @returns {boolean} True when expanded.
     */
    get isExpanded() {
        return this._isExpanded;
    }

    className = 'SlidingContainer';

    /**
     * Returns the observed attributes for the component.
     * @returns {string[]}
     */
    static get observedAttributes() {
        return ['max-width', 'max-height', 'trigger', 'expanded-width', 'expanded-direction', 'expanded-trigger', 'direction', 'variant', 'screen-break-point', 'remove-child-after-close', 'animation-duration', 'animation-easing', 'has-opacity', 'mobile-presentation', 'mobile-break-point', 'backdrop', 'backdrop-dismiss', 'scope', 'sheet-resizable', 'sheet-scope', 'sheet-boundary', 'sheet-height', 'sheet-min-height', 'sheet-max-height'];
    }

    /**
     * Returns the CSS styles for the component.
     * @static
     * @returns {CSSStyleSheet}
     */
    static get cssStyleSheet() {
        return styles;
    }

    /**
     * Sets up the attributes for the component.
     */
    setupAttributes() {
        this.isShadowRoot = 'open';
        this.syncAria();
    }

    /**
     * Starts observing viewport size changes. The observer is intentionally kept
     * alive across attribute redraws and is stopped only when the host disconnects.
     */
    startResizeObservation() {
        if (this._resizeObserver) return;

        this._resizeObserver = new ResizeObserver(this.handleViewportResize);
        this._resizeObserver.observe(document.documentElement);
    }

    /**
     * Stops viewport size observation.
     */
    stopResizeObservation() {
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
    }

    /**
     * Applies the responsive presentation when the viewport changes.
     * @param {ResizeObserverEntry[]} entries Resize observer entries.
     */
    handleViewportResize = (entries) => {
        if (!entries.some((entry) => entry.contentBoxSize) || this.drawingStatus < 3) return;

        this.checkForVariant(this.presentationVariant);
        this.handleExpandedViewportChange();
    };

    /**
     * Binds the currently configured document trigger.
     */
    bindTrigger() {
        this.unbindTrigger();
        this._boundTrigger = this.trigger;
        document.addEventListener(this._boundTrigger, this.triggerEvent);
    }

    /**
     * Unbinds the trigger name that was actually registered.
     */
    unbindTrigger() {
        if (!this._boundTrigger) return;

        document.removeEventListener(this._boundTrigger, this.triggerEvent);
        this._boundTrigger = null;
    }

    /**
     * Binds the optional document trigger used by the expanded panel.
     */
    bindExpandedTrigger() {
        this.unbindExpandedTrigger();
        if (!this.expandedTrigger) return;

        this._boundExpandedTrigger = this.expandedTrigger;
        document.addEventListener(this._boundExpandedTrigger, this.expandedTriggerEvent);
    }

    /**
     * Unbinds the expanded trigger that was actually registered.
     */
    unbindExpandedTrigger() {
        if (!this._boundExpandedTrigger) return;

        document.removeEventListener(this._boundExpandedTrigger, this.expandedTriggerEvent);
        this._boundExpandedTrigger = null;
    }

    /**
     * Executes before drawing the element.
     */
    beforeDraw() {
        this.finishBaseTransitionImmediately();
        this.finishExpandedTransitionImmediately();
        this.animation?.cancel();
        this.nativeAnimation?.cancel();
        this.backdropController.cancelAnimation();
        this.endSheetDrag();
        this.backdropController.remove();
        this.unbindTrigger();
        this.unbindExpandedTrigger();
    }

    /**
     * Cleans up global listeners and portaled layers.
     */
    afterDisconnect() {
        this.finishBaseTransitionImmediately();
        this.animation?.cancel();
        this.nativeAnimation?.cancel();
        this.backdropController.cancelAnimation();
        this.endSheetDrag();
        this.backdropController.remove();
        this.unbindTrigger();
        this.unbindExpandedTrigger();
    }

    /**
     * Draws the component.
     * @param {object} context The context for drawing.
     * @param {object} store The store for drawing.
     * @param {object} params The parameters for drawing.
     * @returns {DocumentFragment}
     */
    draw(context, store, params) {
        this.sheetHandleArea = null;
        const { fragment, elements } = createSlidingContainerView({
            isBottom: this.usesBottomPresentation(),
            isOpen: this._isOpen,
            direction: this.direction,
            maxWidth: this.maxWidth,
            maxHeight: this.maxHeight,
            hasOpacity: this.hasOpacity,
            closeButton: this.htmlCloseButton(),
            sheetHandle: this.sheetResizable ? this.htmlSheetHandle() : null,
            showBackdrop: this.backdrop,
        });

        Object.assign(this, elements);
        return fragment;
    }

    /**
     * Performs actions after the element is drawn on the screen.
     * Attaches an event listener to the document based on the specified trigger.
     * Sets the variant to "over" if the document width is smaller than the screen break point.
     * Calls the checkForVariant method with the current variant.
     */
    afterDraw() {
        this.syncAria();
        this.startResizeObservation();
        this.bindTrigger();
        this.bindExpandedTrigger();

        // if document width is on small screen set variant to over
        this.checkForVariant(this.presentationVariant);
        this.syncExpandedAfterDraw();
    }

    /**
     * Returns whether the effective direction uses bottom drawer presentation.
     * @returns {boolean}
     */
    usesBottomPresentation() {
        return this.presentationDirection === 'bottom';
    }

    /**
     * Returns whether the legacy responsive bottom-sheet adapter is active.
     * This distinction is used only for backwards-compatible backdrop behavior;
     * new bottom drawers should use direction="bottom" and an explicit backdrop.
     * @returns {boolean} True when the legacy adapter is active below its breakpoint.
     */
    usesLegacyBottomSheetPresentation() {
        return this.mobilePresentation === 'bottom-sheet'
            && window.innerWidth <= +this.mobileBreakPoint;
    }

    /**
     * Returns whether the current effective presentation is a bottom drawer.
     * Kept as a public compatibility alias for existing applications.
     * @returns {boolean} True for direct and responsive bottom presentations.
     */
    isBottomSheet() {
        return this.usesBottomPresentation();
    }

    /**
     * Handles backdrop click dismissal.
     * @param {MouseEvent} e The click event.
     */
    handleBackdropClick = (e) => {
        if (this.usesBottomPresentation() && (this.isEventInsideSheet(e)
            || Date.now() < this._sheetIgnoreDismissUntil
            || Date.now() < this._sheetBackdropHandledUntil)) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        if (this.backdropController.owns(e.target)) {
            this.close(e);
        }
    };

    /**
     * Keeps closed base content out of pointer and keyboard interaction.
     * @param {boolean} isInteractive Whether the base panel is available.
     */
    setBaseInteractionState(isInteractive) {
        if (!this.nativeElement) return;

        this.nativeElement.toggleAttribute('inert', !isInteractive);
        this.nativeElement.setAttribute('aria-hidden', isInteractive ? 'false' : 'true');
    }

    /**
     * Sync ARIA attributes on host.
     */
    syncAria() {
        if (!this.hasAttribute('role')) {
            this.setAriaState({ role: 'region' });
        }

        this.setAriaState({ hidden: !this.isOpen });

        const ariaLabel = this.getAttribute('aria-label');
        const label = this.getAttribute('label');
        if (!ariaLabel && label) {
            this.setAriaState({ label });
        }

        this.setBaseInteractionState(this._isOpen);
    }

    /**
     * Creates and returns a styled close button element with an icon,
     * including an event listener to trigger the close method.
     * @returns {HTMLElement} The close button element configured with styles, an icon, and event listener.
     */
    htmlCloseButton() {
        let closeButton = document.createElement('wje-button');
        closeButton.setAttribute('part', 'close-button');
        closeButton.classList.add('close-button');

        let icon = document.createElement('wje-icon');
        icon.setAttribute('slot', 'icon-only');
        icon.setAttribute('name', 'x');

        closeButton.append(icon);

        event.addListener(closeButton, 'wje-button:click', null,(e) => {
            this.close();
        });

        return closeButton;
    }

    /**
     * Creates the mobile bottom sheet resize handle.
     * @returns {HTMLElement} The resize handle element.
     */
    htmlSheetHandle() {
        const handleArea = document.createElement('div');
        handleArea.setAttribute('part', 'sheet-handle-area');
        handleArea.setAttribute('role', 'separator');
        handleArea.setAttribute('aria-orientation', 'horizontal');
        handleArea.classList.add('sliding-container-sheet-handle-area');

        const handle = document.createElement('div');
        handle.setAttribute('part', 'sheet-handle');
        handle.classList.add('sliding-container-sheet-handle');

        handleArea.append(handle);
        handleArea.addEventListener('pointerdown', this.handleSheetDragStart, { capture: true });
        handleArea.addEventListener('touchstart', this.handleSheetTouchStart, { capture: true, passive: false });
        handleArea.addEventListener('click', this.stopSheetHandleEvent, { capture: true });
        handleArea.addEventListener('mousedown', this.stopSheetHandleEvent, { capture: true });
        handleArea.addEventListener('mouseup', this.stopSheetHandleEvent, { capture: true });

        this.sheetHandleArea = handleArea;

        return handleArea;
    }

    /**
     * Retrieves the parent element of the current element.
     * If the parent element is not found, it attempts to find the root host element.
     * @returns {Element|null} The parent element or the root host element if no parent exists. Returns null if neither is found.
     */
    getParentElement() {
        let parentElement = this.parentElement;

        if (!parentElement) {
            parentElement = this.getRootNode().host;
        }

        return parentElement;
    }

    /**
     * Finds the closest element across shadow DOM boundaries.
     * @param {string} selector The selector to match.
     * @returns {Element|null} The matched composed ancestor.
     */
    getClosestComposedElement(selector) {
        let currentElement = this;

        while (currentElement) {
            let matches = false;

            try {
                matches = currentElement instanceof Element && currentElement.matches(selector);
            } catch {
                return null;
            }

            if (matches) {
                return currentElement;
            }

            if (currentElement.parentElement) {
                currentElement = currentElement.parentElement;
                continue;
            }

            const root = currentElement.getRootNode?.();
            currentElement = root instanceof ShadowRoot ? root.host : null;
        }

        return null;
    }

    /**
     * Returns the viewport frame.
     * @returns {{top: number, left: number, right: number, bottom: number, width: number, height: number}}
     */
    getViewportFrame() {
        return {
            top: 0,
            left: 0,
            right: window.innerWidth,
            bottom: window.innerHeight,
            width: window.innerWidth,
            height: window.innerHeight,
        };
    }

    /**
     * Returns the element that bounds the mobile bottom sheet panel.
     * @returns {Element|null} The scope element, or null for viewport scope.
     */
    getBottomScopeElement() {
        if (this.presentationScope === 'viewport') return null;
        if (this.presentationScope === 'parent') return this.getParentElement();
        if (this.sheetBoundary) return this.getClosestComposedElement(this.sheetBoundary);

        return this.getParentElement();
    }

    /**
     * Returns the frame used by the mobile bottom sheet panel.
     * @returns {{top: number, left: number, right: number, bottom: number, width: number, height: number}}
     */
    getBottomScopeFrame() {
        const scopeElement = this.getBottomScopeElement();
        if (!scopeElement) return this.getViewportFrame();

        const rect = scopeElement.getBoundingClientRect();
        const top = Math.max(rect.top, 0);
        const left = Math.max(rect.left, 0);
        const right = Math.min(rect.right, window.innerWidth);
        const bottom = Math.min(rect.bottom, window.innerHeight);
        const width = right - left;
        const height = bottom - top;

        if (width <= 0 || height <= 0) {
            return this.getViewportFrame();
        }

        return {
            top,
            left,
            right,
            bottom,
            width,
            height,
        };
    }

    /**
     * Applies a fixed frame to a bottom sheet layer.
     * @param {HTMLElement|SlidingContainer} element The element to update.
     * @param {object} frame The frame to apply.
     */
    setBottomLayerFrame(element, frame) {
        element.style.top = frame.top + 'px';
        element.style.left = frame.left + 'px';
        element.style.right = 'auto';
        element.style.bottom = 'auto';
        element.style.width = frame.width + 'px';
        element.style.height = frame.height + 'px';
    }

    /**
     * Removes fixed frame styles from a bottom sheet layer.
     * @param {HTMLElement|SlidingContainer} element The element to reset.
     */
    resetBottomLayerFrame(element) {
        element?.style.removeProperty('top');
        element?.style.removeProperty('left');
        element?.style.removeProperty('right');
        element?.style.removeProperty('bottom');
        element?.style.removeProperty('width');
        element?.style.removeProperty('height');
    }

    /**
     * Adjusts the position and dimensions of the current element based on the specified variant.
     *
     * The method handles modifications to the element's positioning style, aligns it relative to its parent,
     * and manages alignment to its siblings based on the specified direction.
     * @param {string} variant The variant to determine how the element should be updated. For example, when set to 'over', specific adjustments to the position and size are performed.
     * @returns {void} No value is returned, the method modifies the element's style properties directly.
     */
    checkForVariant(variant) {
        if (this.usesBottomPresentation()) {
            if (this._isExpanded || this._expandedTarget) {
                this.resetExpanded();
            }
            this.resetSideOverVariant();
            this.applyBottomPresentation();
            this.backdropController.setOpen(this.classList.contains('open') || this._isOpen);
            return;
        }

        this.resetBottomPresentation();

        if (variant === 'over') {
            this.setSideOverVariant();
        } else {
            this.resetSideOverVariant();
        }

        this.backdropController.setOpen(this.classList.contains('open') || this._isOpen);
    }

    /**
     * Stores an inline style value so side overlay layout can restore it later.
     * @param {CSSStyleDeclaration} style The style declaration to read.
     * @param {string} property The CSS property name.
     * @returns {string} The original inline value.
     */
    getInlineStyleValue(style, property) {
        return style.getPropertyValue(property);
    }

    /**
     * Restores an inline style value previously owned by the consumer.
     * @param {CSSStyleDeclaration} style The style declaration to update.
     * @param {string} property The CSS property name.
     * @param {string} value The original inline value.
     */
    restoreInlineStyleValue(style, property, value) {
        if (value) {
            style.setProperty(property, value);
        } else {
            style.removeProperty(property);
        }
    }

    /**
     * Applies fixed positioning used by the side overlay variant.
     */
    setSideOverVariant() {
        const parentElement = this.getParentElement();
        if (!parentElement || !this.wrapperDiv) return;

        if (!this._sideOverStyleSnapshot) {
            this._sideOverStyleSnapshot = {
                position: this.getInlineStyleValue(this.style, 'position'),
                top: this.getInlineStyleValue(this.style, 'top'),
                left: this.getInlineStyleValue(this.style, 'left'),
                right: this.getInlineStyleValue(this.style, 'right'),
                height: this.getInlineStyleValue(this.style, 'height'),
                wrapperHeight: this.getInlineStyleValue(this.wrapperDiv.style, 'height'),
            };
        }

        const computedParentStyle = window.getComputedStyle(parentElement);
        const parentBounds = parentElement.getBoundingClientRect();
        const computedHeight = Number.parseFloat(computedParentStyle.height);
        const parentHeight = Number.isFinite(computedHeight) ? computedHeight : parentBounds.height;
        const height = parentHeight + +this.addToHeight;

        this.style.position = 'fixed';
        this.style.height = height + 'px';
        this.wrapperDiv.style.height = height + 'px';
        this.style.top = parentBounds.top + 'px';
        this.style.removeProperty('left');
        this.style.removeProperty('right');

        const leftSiblingBounds = this.previousElementSibling?.getBoundingClientRect();
        const rightSiblingBounds = this.nextElementSibling?.getBoundingClientRect();

        if (this.direction === 'right') {
            const left = leftSiblingBounds ? leftSiblingBounds.right : parentBounds.left;
            this.style.left = left + 'px';
        } else {
            const right = rightSiblingBounds
                ? window.innerWidth - rightSiblingBounds.left
                : window.innerWidth - parentBounds.right;
            this.style.right = right + 'px';
        }
    }

    /**
     * Restores styles changed by the side overlay variant.
     */
    resetSideOverVariant() {
        const snapshot = this._sideOverStyleSnapshot;
        if (!snapshot) return;

        for (const property of ['position', 'top', 'left', 'right', 'height']) {
            this.restoreInlineStyleValue(this.style, property, snapshot[property]);
        }
        if (this.wrapperDiv) {
            this.restoreInlineStyleValue(this.wrapperDiv.style, 'height', snapshot.wrapperHeight);
        }

        this._sideOverStyleSnapshot = null;
    }

    /**
     * Returns whether the named expanded slot contains renderable content.
     * @returns {boolean} True when expanded content is assigned.
     */
    hasExpandedContent() {
        return Boolean(this.expandedSlotElement?.assignedNodes({ flatten: true }).some((node) => {
            return node.nodeType === Node.ELEMENT_NODE
                || (node.nodeType === Node.TEXT_NODE && node.textContent.trim());
        }));
    }

    /**
     * Returns the rendered base width in pixels.
     * @returns {number} The base panel width.
     */
    getBaseWidthInPixels() {
        const width = this.nativeInner?.getBoundingClientRect().width
            || this.panelsAnchor?.getBoundingClientRect().width
            || 0;

        return Number.isFinite(width) ? width : 0;
    }

    /**
     * Resolves expanded-width through browser layout. Percentages are resolved
     * against the base panel width because the probe lives in the panels anchor.
     * @returns {number} The expanded panel width in pixels, or zero when invalid.
     */
    resolveExpandedWidth() {
        const value = this.expandedWidth?.trim();
        if (!value || !this.panelsAnchor) return 0;
        if (['auto', 'min-content', 'max-content', 'stretch'].includes(value)) return 0;
        if (globalThis.CSS?.supports && !CSS.supports('width', value)) return 0;

        const probe = document.createElement('div');
        probe.style.position = 'absolute';
        probe.style.visibility = 'hidden';
        probe.style.pointerEvents = 'none';
        probe.style.boxSizing = 'border-box';
        probe.style.width = value;
        probe.style.height = '0px';
        this.panelsAnchor.append(probe);

        const width = probe.getBoundingClientRect().width;
        probe.remove();

        return Number.isFinite(width) && width > 0 ? width : 0;
    }

    /**
     * Positions the expanded clip next to the requested physical side.
     * @param {number} baseWidth Base panel width in pixels.
     */
    positionExpandedPanel(baseWidth) {
        if (!this.expandedElement || !this.expandedInner) return;

        this.expandedElement.style.removeProperty('left');
        this.expandedElement.style.removeProperty('right');
        this.expandedInner.style.removeProperty('left');
        this.expandedInner.style.removeProperty('right');

        if (this.expandedDirection === 'left') {
            this.expandedElement.style.right = baseWidth + 'px';
            this.expandedInner.style.right = '0px';
        } else {
            this.expandedElement.style.left = baseWidth + 'px';
            this.expandedInner.style.left = '0px';
        }
    }

    /**
     * Returns whether the expanded panel replaces the base panel in the page
     * flow. This happens when it opens on the outer side of the component. The
     * outer side is opposite to the base panel's sliding direction.
     * @returns {boolean} True when the expanded panel owns the flow reservation.
     */
    swapsExpandedFlowPanel() {
        return this.presentationVariant !== 'over' && this.expandedDirection !== this.direction;
    }

    /**
     * Resolves the panels anchor offset for the expanded in-place layout.
     * @param {number} baseWidth Base panel width in pixels.
     * @param {number} expandedWidth Expanded panel width in pixels.
     * @returns {number} Anchor offset from the host's left edge.
     */
    getExpandedAnchorLeft(baseWidth, expandedWidth) {
        if (!this.swapsExpandedFlowPanel()) return 0;

        return this.direction === 'right' ? expandedWidth : -baseWidth;
    }

    /**
     * Restores the base panels anchor used by the existing side presentation.
     */
    resetPanelsAnchor() {
        if (!this.panelsAnchor) return;

        this.panelsAnchor.style.removeProperty('left');
        this.panelsAnchor.style.removeProperty('right');
        if (this.direction === 'right') {
            this.panelsAnchor.style.right = '0px';
        } else {
            this.panelsAnchor.style.left = '0px';
        }
    }

    /**
     * Makes expanded content available to assistive technology and interaction.
     */
    showExpandedContent() {
        if (!this.expandedElement) return;

        this.expandedElement.style.visibility = 'visible';
        this.expandedElement.removeAttribute('inert');
        this.expandedElement.setAttribute('aria-hidden', 'false');
    }

    /**
     * Hides expanded content without detaching its slotted DOM.
     */
    hideExpandedContent() {
        if (!this.expandedElement) return;

        this.expandedElement.setAttribute('inert', '');
        this.expandedElement.setAttribute('aria-hidden', 'true');
        this.expandedElement.style.visibility = 'hidden';
    }

    /**
     * Applies a settled expanded or base-only layout.
     * @param {boolean} isExpanded Whether the expanded panel is open.
     * @param {number} [baseWidth] Base panel width in pixels.
     * @param {number} [expandedWidth] Expanded panel width in pixels.
     */
    applyExpandedState(
        isExpanded,
        baseWidth = this.getBaseWidthInPixels(),
        expandedWidth = this.resolveExpandedWidth()
    ) {
        if (!this.panelsAnchor || !this.expandedElement || !this.expandedInner) return;

        const expanded = Boolean(isExpanded && this._isOpen && !this.usesBottomPresentation());
        this._expandedWidthPixels = expandedWidth;
        this.panelsAnchor.style.width = baseWidth + 'px';
        this.expandedInner.style.width = expandedWidth + 'px';
        this.positionExpandedPanel(baseWidth);
        this.resetPanelsAnchor();

        if (expanded && this.swapsExpandedFlowPanel()) {
            this.transparentDiv.style.width = expandedWidth + 'px';
            this.panelsAnchor.style.removeProperty('right');
            this.panelsAnchor.style.left = this.getExpandedAnchorLeft(baseWidth, expandedWidth) + 'px';
        } else {
            this.transparentDiv.style.width = this._isOpen ? baseWidth + 'px' : '0px';
        }

        this.expandedElement.style.width = expanded ? expandedWidth + 'px' : '0px';
        this._isExpanded = expanded;
        this._expandedTarget = expanded;
        this.classList.toggle('expanded', expanded);

        if (expanded) {
            this.showExpandedContent();
        } else {
            this.hideExpandedContent();
        }
    }

    /**
     * Returns whether the expanded panel can fit in the current browser viewport.
     * @returns {boolean} True when expansion is available.
     */
    canExpand() {
        if (!this._isOpen
            || this._isClosing
            || !this.classList.contains('open')
            || this.usesBottomPresentation()
            || !this.hasExpandedContent()) return false;

        const baseWidth = this.getBaseWidthInPixels();
        const expandedWidth = this.resolveExpandedWidth();
        const baseBounds = this.nativeInner?.getBoundingClientRect();
        if (!baseBounds || baseWidth <= 0 || expandedWidth <= 0) return false;

        let left;
        let right;

        if (this._isExpanded) {
            const expandedBounds = this.expandedInner?.getBoundingClientRect();
            if (!expandedBounds) return false;
            left = Math.min(baseBounds.left, expandedBounds.left);
            right = Math.max(baseBounds.right, expandedBounds.right);
        } else if (this.presentationVariant === 'over') {
            if (this.expandedDirection === 'left') {
                left = baseBounds.left - expandedWidth;
                right = baseBounds.right;
            } else {
                left = baseBounds.left;
                right = baseBounds.right + expandedWidth;
            }
        } else if (this.direction === 'left') {
            left = baseBounds.left - expandedWidth;
            right = baseBounds.right;
        } else {
            left = baseBounds.left;
            right = baseBounds.right + expandedWidth;
        }

        const viewport = this.getViewportFrame();
        const tolerance = 0.5;
        return left >= viewport.left - tolerance && right <= viewport.right + tolerance;
    }

    /**
     * Reapplies expanded geometry after an observed attribute redraw.
     */
    syncExpandedAfterDraw() {
        const wasExpanded = this._isExpanded;
        const baseWidth = this.getBaseWidthInPixels();
        const expandedWidth = this.resolveExpandedWidth();
        this.applyExpandedState(wasExpanded, baseWidth, expandedWidth);

        if (wasExpanded && !this.canExpand()) {
            void this.collapse();
        }
    }

    /**
     * Collapses expansion when viewport or responsive mode makes it unavailable.
     */
    handleExpandedViewportChange() {
        if (!this._isExpanded && !this._expandedTarget) return;

        if (this.usesBottomPresentation()) {
            this.resetExpanded();
            return;
        }

        if (this._isExpanded && !this._expandedTransition) {
            this.applyExpandedState(true, this.getBaseWidthInPixels(), this.resolveExpandedWidth());
        }

        if (!this.canExpand()) {
            void this.collapse();
        }
    }

    /**
     * Applies the mobile bottom sheet layout.
     */
    applyBottomPresentation() {
        const scopeFrame = this.getBottomScopeFrame();
        this._bottomScopeFrame = scopeFrame;

        this.classList.add('direction-bottom', 'bottom-sheet');
        this.style.position = 'fixed';
        this.setBottomLayerFrame(this, scopeFrame);
        this.setBottomLayerFrame(this.wrapperDiv, scopeFrame);

        this.transparentDiv.style.width = 0;

        this.nativeElement.style.left = 0;
        this.nativeElement.style.right = 'auto';
        this.nativeElement.style.bottom = 0;
        this.nativeElement.style.top = 'auto';
        this.nativeElement.style.width = scopeFrame.width + 'px';
        if (this._sheetDrag) {
            this.nativeElement.style.maxHeight = this.getSheetMaxHeightInPixels() + 'px';
        } else {
            this.setSheetHeight();
        }
        this.setBottomVisualState(this.classList.contains('open'));

        this.nativeInner.style.width = '100%';
        this.nativeInner.style.maxHeight = this.nativeElement.style.maxHeight;
    }

    /**
     * Removes layout styles managed by the mobile bottom sheet mode.
     */
    resetBottomPresentation() {
        if (!this.classList.contains('direction-bottom')) return;

        this.classList.remove('direction-bottom', 'bottom-sheet');
        this.style.removeProperty('position');
        this.resetBottomLayerFrame(this);

        this.resetBottomLayerFrame(this.wrapperDiv);
        if (this.transparentDiv) {
            this.transparentDiv.style.width = this._isOpen ? this.maxWidth : 0;
        }
        this.nativeElement?.style.removeProperty('left');
        this.nativeElement?.style.removeProperty('right');
        this.nativeElement?.style.removeProperty('bottom');
        this.nativeElement?.style.removeProperty('top');
        this.nativeElement?.style.removeProperty('height');
        this.nativeElement?.style.removeProperty('max-height');
        this.nativeElement?.style.removeProperty('transform');
        if (this.nativeElement) {
            if (this.direction === 'right') {
                this.nativeElement.style.right = 0;
            } else {
                this.nativeElement.style.left = 0;
            }
            this.nativeElement.style.width = this._isOpen ? this.maxWidth : 0;
        }
        this.nativeInner?.style.removeProperty('max-height');
        if (this.nativeInner) {
            this.nativeInner.style.width = this.maxWidth;
        }
        this._bottomScopeFrame = null;
        this.endSheetDrag();
    }

    /**
     * Applies the current visual open or closed state to the mobile bottom sheet.
     * @param {boolean} isOpen True when the sheet should be visible.
     */
    setBottomVisualState(isOpen) {
        this.nativeElement.style.transform = isOpen ? 'translateY(0)' : 'translateY(100%)';

        if (this.hasOpacity) {
            this.nativeElement.style.opacity = isOpen ? 1 : 0;
        }

        this.backdropController.setOpen(isOpen);
    }

    /**
     * Gets the CSS height limit used for the mobile bottom sheet.
     * @returns {string} The CSS max height.
     */
    getSheetMaxHeight() {
        return this.sheetMaxHeight || this.maxHeight;
    }

    /**
     * Applies the configured bottom sheet opening height.
     */
    setSheetHeight() {
        const maxHeightInPixels = this.getSheetMaxHeightInPixels();

        this.nativeElement.style.maxHeight = maxHeightInPixels + 'px';

        if (!this.sheetHeight) {
            this.nativeElement.style.removeProperty('height');
            return;
        }

        const minHeight = Math.min(this.getSheetMinHeightInPixels(), maxHeightInPixels);
        const height = resolveDrawerHeight(this.sheetHeight, maxHeightInPixels, {
            width: window.innerWidth,
            height: window.innerHeight,
        });
        const clampedHeight = clampDrawerSize(height, minHeight, maxHeightInPixels);

        this.nativeElement.style.height = clampedHeight + 'px';
    }

    /**
     * Resolves the sheet minimum height in pixels.
     * @returns {number} The minimum height.
     */
    getSheetMinHeightInPixels() {
        const availableHeight = this.getBottomAvailableHeight();

        return Math.min(
            resolveDrawerHeight(this.sheetMinHeight || '30vh', availableHeight * 0.3, {
                width: window.innerWidth,
                height: window.innerHeight,
            }),
            availableHeight
        );
    }

    /**
     * Resolves the sheet maximum height in pixels.
     * @returns {number} The maximum height.
     */
    getSheetMaxHeightInPixels() {
        const availableHeight = this.getBottomAvailableHeight();

        return Math.min(
            resolveDrawerHeight(this.getSheetMaxHeight(), availableHeight * 0.9, {
                width: window.innerWidth,
                height: window.innerHeight,
            }),
            availableHeight
        );
    }

    /**
     * Returns available height for the bottom sheet scope.
     * @returns {number} Pixel height that can be used inside the current sheet frame.
     */
    getBottomAvailableHeight() {
        return this._bottomScopeFrame?.height || this.getBottomScopeFrame().height || window.innerHeight;
    }

    /**
     * Stops a handle interaction from dismissing the bottom sheet.
     * @param {Event} e The interaction emitted by the resize handle.
     */
    stopSheetHandleEvent = (e) => {
        if (!this.usesBottomPresentation()) return;

        this._sheetIgnoreDismissUntil = Date.now() + 500;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
    };

    /**
     * Checks whether an event came from the resize handle.
     * @param {Event} e The event to check.
     * @returns {boolean} True when the event belongs to the resize handle.
     */
    isSheetHandleEvent(e) {
        if (!e?.composedPath) return false;

        return e.composedPath().includes(this.sheetHandleArea);
    }

    /**
     * Checks whether an event happened inside the visible bottom sheet.
     * @param {Event} e The event to check.
     * @returns {boolean} True when the event coordinates are inside the sheet.
     */
    isEventInsideSheet(e) {
        if (!this.nativeElement || typeof e?.clientX !== 'number' || typeof e?.clientY !== 'number') return false;

        const rect = this.nativeElement.getBoundingClientRect();

        return e.clientX >= rect.left
            && e.clientX <= rect.right
            && e.clientY >= rect.top
            && e.clientY <= rect.bottom;
    }

    /**
     * Prevents scroll events from reaching the page behind the bottom sheet.
     * @param {Event} e The scroll event.
     */
    preventBackdropScroll = (e) => {
        if (!this.usesBottomPresentation() || !this.classList.contains('open')) return;

        e.preventDefault();
        e.stopPropagation();
    };

    /**
     * Stores the initial drag state for the mobile bottom sheet.
     * @param {number|string} pointerId The pointer identifier.
     * @param {number} startY The starting Y coordinate.
     */
    startSheetDrag(pointerId, startY) {
        if (this.classList.contains('open')) {
            this.setBottomVisualState(true);
        }

        this.finishBaseTransitionImmediately();
        this.animation?.cancel();
        this.nativeAnimation?.cancel();
        this.animation = null;
        this.nativeAnimation = null;

        const fallbackHeight = resolveDrawerHeight(this.sheetHeight, window.innerHeight * 0.5, {
            width: window.innerWidth,
            height: window.innerHeight,
        });
        const currentHeight = this.nativeElement.getBoundingClientRect().height || fallbackHeight;

        this._sheetDrag = {
            pointerId,
            startY,
            startHeight: currentHeight,
            minHeight: this.getSheetMinHeightInPixels(),
            maxHeight: this.getSheetMaxHeightInPixels(),
            hasMoved: false,
        };
    }

    /**
     * Applies a new height while the bottom sheet is being dragged.
     * @param {number} clientY The current Y coordinate.
     */
    updateSheetDrag(clientY) {
        const distance = this._sheetDrag.startY - clientY;
        if (Math.abs(distance) > 3) {
            this._sheetDrag.hasMoved = true;
        }

        const height = clampDrawerSize(
            this._sheetDrag.startHeight + distance,
            this._sheetDrag.minHeight,
            this._sheetDrag.maxHeight
        );

        this.nativeElement.style.height = height + 'px';
    }

    /**
     * Starts mobile bottom sheet resizing.
     * @param {PointerEvent} e The pointer event.
     */
    handleSheetDragStart = (e) => {
        if (!this.sheetResizable || !this.usesBottomPresentation()) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();

        this._sheetIgnoreDismissUntil = Date.now() + 500;

        if (this._sheetDrag) return;

        this.startSheetDrag(e.pointerId, e.clientY);

        try {
            this.sheetHandleArea?.setPointerCapture(e.pointerId);
        } catch {
            // Synthetic pointer events in tests may not own a pointer capture.
        }

        window.addEventListener('pointermove', this.handleSheetDragMove);
        window.addEventListener('pointerup', this.handleSheetDragEnd);
        window.addEventListener('pointercancel', this.handleSheetDragEnd);
    };

    /**
     * Starts mobile bottom sheet resizing from a touch event.
     * @param {TouchEvent} e The touch event.
     */
    handleSheetTouchStart = (e) => {
        if (!this.sheetResizable || !this.usesBottomPresentation() || this._sheetDrag) return;

        const touch = e.touches?.[0];
        if (!touch) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();

        this._sheetIgnoreDismissUntil = Date.now() + 500;
        this.startSheetDrag('touch', touch.clientY);

        window.addEventListener('touchmove', this.handleSheetTouchMove, { passive: false });
        window.addEventListener('touchend', this.handleSheetTouchEnd, { passive: false });
        window.addEventListener('touchcancel', this.handleSheetTouchEnd, { passive: false });
    };

    /**
     * Starts tracking a possible backdrop dismiss tap.
     * @param {PointerEvent} e The pointer event.
     */
    handleBackdropPointerStart = (e) => {
        if (!this.usesBottomPresentation()
            || !this.backdropController.owns(e.target)
            || this.isEventInsideSheet(e)
            || Date.now() < this._sheetIgnoreDismissUntil) {
            this._sheetBackdropGesture = null;
            return;
        }

        this._sheetBackdropGesture = {
            pointerId: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            hasMoved: false,
        };
    };

    /**
     * Tracks movement during a possible backdrop dismiss tap.
     * @param {PointerEvent} e The pointer event.
     */
    handleBackdropPointerMove = (e) => {
        if (!this._sheetBackdropGesture || e.pointerId !== this._sheetBackdropGesture.pointerId) return;

        const distanceX = Math.abs(e.clientX - this._sheetBackdropGesture.startX);
        const distanceY = Math.abs(e.clientY - this._sheetBackdropGesture.startY);

        if (distanceX > 8 || distanceY > 8) {
            this._sheetBackdropGesture.hasMoved = true;
        }
    };

    /**
     * Finishes a possible backdrop dismiss tap.
     * @param {PointerEvent} e The pointer event.
     */
    handleBackdropPointerEnd = (e) => {
        if (!this._sheetBackdropGesture || e.pointerId !== this._sheetBackdropGesture.pointerId) return;

        const shouldClose = !this._sheetBackdropGesture.hasMoved
            && this.backdropController.owns(e.target)
            && !this.isEventInsideSheet(e)
            && Date.now() >= this._sheetIgnoreDismissUntil;

        this._sheetBackdropGesture = null;
        this._sheetBackdropHandledUntil = Date.now() + 350;

        if (shouldClose) {
            e.preventDefault();
            e.stopPropagation();
            this.close(e);
        }
    };

    /**
     * Cancels a possible backdrop dismiss tap.
     */
    handleBackdropPointerCancel = () => {
        this._sheetBackdropGesture = null;
        this._sheetBackdropHandledUntil = Date.now() + 350;
    };

    /**
     * Resizes the mobile bottom sheet during dragging.
     * @param {PointerEvent} e The pointer event.
     */
    handleSheetDragMove = (e) => {
        if (!this._sheetDrag || e.pointerId !== this._sheetDrag.pointerId) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();

        this.updateSheetDrag(e.clientY);
    };

    /**
     * Resizes the mobile bottom sheet during touch dragging.
     * @param {TouchEvent} e The touch event.
     */
    handleSheetTouchMove = (e) => {
        if (!this._sheetDrag || this._sheetDrag.pointerId !== 'touch') return;

        const touch = e.touches?.[0];
        if (!touch) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();

        this.updateSheetDrag(touch.clientY);
    };

    /**
     * Ends mobile bottom sheet resizing.
     * @param {PointerEvent} e The pointer event.
     */
    handleSheetDragEnd = (e) => {
        const hasMoved = this._sheetDrag?.hasMoved;

        if (this._sheetDrag && e?.pointerId === this._sheetDrag.pointerId) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();

            try {
                this.sheetHandleArea?.releasePointerCapture(e.pointerId);
            } catch {
                // Synthetic pointer events in tests may not own a pointer capture.
            }
        }

        if (hasMoved) {
            this._sheetIgnoreDismissUntil = Date.now() + 350;
        } else {
            this._sheetIgnoreDismissUntil = Date.now() + 500;
        }

        this.endSheetDrag();
    };

    /**
     * Ends mobile bottom sheet resizing from a touch event.
     * @param {TouchEvent} e The touch event.
     */
    handleSheetTouchEnd = (e) => {
        if (!this._sheetDrag || this._sheetDrag.pointerId !== 'touch') return;

        const hasMoved = this._sheetDrag.hasMoved;

        if (e.type !== 'touchcancel') {
            e.preventDefault();
        }
        e.stopPropagation();
        if (e.type !== 'touchcancel') {
            e.stopImmediatePropagation?.();
        }

        this._sheetIgnoreDismissUntil = Date.now() + (hasMoved ? 350 : 700);
        this.endSheetDrag();
    };

    /**
     * Cleans up mobile bottom sheet resizing listeners.
     */
    endSheetDrag() {
        window.removeEventListener('pointermove', this.handleSheetDragMove);
        window.removeEventListener('pointerup', this.handleSheetDragEnd);
        window.removeEventListener('pointercancel', this.handleSheetDragEnd);
        window.removeEventListener('touchmove', this.handleSheetTouchMove);
        window.removeEventListener('touchend', this.handleSheetTouchEnd);
        window.removeEventListener('touchcancel', this.handleSheetTouchEnd);
        this._sheetDrag = null;
    }

    /**
     * Triggers the event based on the target element.
     * If the target element is different from the last caller, it refreshes the children by calling the `open` method.
     * If the target element is the same as the last caller, it toggles the state by calling the `toggle` method.
     * @param {Event} e The event object.
     */
    triggerEvent = async (e) => {
        if (this._lastCaller && this._lastCaller !== e.composedPath()[0]) {
            // same oppener event but different caller so just refresh inner content
            await this.open(e);
        } else {
            // came caller so toggle
            await this.toggle(e);
        }

        this._lastCaller = e.composedPath()[0];
    };

    /**
     * Toggles the expanded panel from its optional document trigger.
     * @param {Event} e The event that triggered expansion.
     */
    expandedTriggerEvent = async (e) => {
        await this.toggleExpanded(e);
    };

    /**
     * Executes before the expanded panel opens.
     * @param {Event} [e] The triggering event.
     */
    beforeExpand(e) {
        // Hook for extending behavior before expansion.
    }

    /**
     * Executes after the expanded panel opens.
     * @param {Event} [e] The triggering event.
     */
    afterExpand(e) {
        // Hook for extending behavior after expansion.
    }

    /**
     * Executes before the expanded panel closes.
     * @param {Event} [e] The triggering event.
     */
    beforeCollapse(e) {
        // Hook for extending behavior before collapsing expansion.
    }

    /**
     * Executes after the expanded panel closes.
     * @param {Event} [e] The triggering event.
     */
    afterCollapse(e) {
        // Hook for extending behavior after collapsing expansion.
    }

    /**
     * Cancels the active expanded transition and settles its promise as interrupted.
     */
    cancelExpandedTransition() {
        const transition = this._expandedTransition;
        if (!transition) return;

        this._expandedTransition = null;
        for (const animation of transition.animations) {
            animation.onfinish = null;
            animation.oncancel = null;
            animation.cancel();
        }
        transition.resolve(false);
    }

    /**
     * Completes a specific expanded transition and applies its settled geometry.
     * @param {object} transition The active transition record.
     */
    completeExpandedTransition(transition) {
        if (this._expandedTransition !== transition) return;

        this._expandedTransition = null;
        for (const animation of transition.animations) {
            animation.onfinish = null;
            animation.oncancel = null;
            animation.cancel();
        }

        this.applyExpandedState(transition.target, transition.baseWidth, transition.expandedWidth);

        // The viewport can change while the animation is running. Validate the
        // settled union rather than relying on a mid-animation projection whose
        // base panel is moving in the right/in-place layout.
        if (transition.target && !this.canExpand()) {
            this.applyExpandedState(false, transition.baseWidth, transition.expandedWidth);
            transition.resolve(false);
            return;
        }

        transition.resolve(true);
    }

    /**
     * Immediately completes an in-flight expanded transition before a redraw.
     */
    finishExpandedTransitionImmediately() {
        if (this._expandedTransition) {
            this.completeExpandedTransition(this._expandedTransition);
        }
    }

    /**
     * Animates the optional expanded panel to its requested state.
     * @param {boolean} target True to expand, false to collapse.
     * @param {number} baseWidth Base panel width in pixels.
     * @param {number} expandedWidth Expanded panel width in pixels.
     * @returns {Promise<boolean>} True when the transition completed.
     */
    animateExpandedTransition(target, baseWidth, expandedWidth) {
        const currentSpacerWidth = this.transparentDiv.getBoundingClientRect().width;
        const wrapperBounds = this.wrapperDiv.getBoundingClientRect();
        const anchorBounds = this.panelsAnchor.getBoundingClientRect();
        const expandedBounds = this.expandedElement.getBoundingClientRect();
        const expandedInnerBounds = this.expandedInner.getBoundingClientRect();
        const currentAnchorLeft = anchorBounds.left - wrapperBounds.left;
        // Animate the CSS width, not the border-box rect. A consumer can add a
        // border through part="expanded"; in the collapsed state that leaves a
        // non-zero rect even though the animated width is still 0px. Treating
        // that border as opened content would keep the inner panel at its final
        // position and only reveal it while the clip grows.
        const inlineExpandedWidth = Number.parseFloat(this.expandedElement.style.width);
        const computedExpandedWidth = Number.parseFloat(getComputedStyle(this.expandedElement).width);
        const currentExpandedWidth = this._expandedTransition && Number.isFinite(computedExpandedWidth)
            ? computedExpandedWidth
            : Number.isFinite(inlineExpandedWidth)
                ? inlineExpandedWidth
                : Number.isFinite(computedExpandedWidth)
                    ? computedExpandedWidth
                    : expandedBounds.width;
        const currentExpandedInnerRight = expandedBounds.right - expandedInnerBounds.right;

        this.cancelExpandedTransition();

        this._expandedTarget = target;
        this._expandedWidthPixels = expandedWidth;
        this.panelsAnchor.style.width = baseWidth + 'px';
        this.positionExpandedPanel(baseWidth);
        this.expandedInner.style.width = expandedWidth + 'px';
        this.expandedElement.style.visibility = 'visible';

        if (target) {
            this.showExpandedContent();
        } else {
            this.expandedElement.setAttribute('inert', '');
            this.expandedElement.setAttribute('aria-hidden', 'true');
        }

        this.transparentDiv.style.width = currentSpacerWidth + 'px';
        this.panelsAnchor.style.removeProperty('right');
        this.panelsAnchor.style.left = currentAnchorLeft + 'px';
        this.expandedElement.style.width = currentExpandedWidth + 'px';

        const swapsFlowPanel = this.swapsExpandedFlowPanel();
        const targetSpacerWidth = target && swapsFlowPanel ? expandedWidth : baseWidth;
        const targetAnchorLeft = target && swapsFlowPanel
            ? this.getExpandedAnchorLeft(baseWidth, expandedWidth)
            : 0;
        const targetExpandedWidth = target ? expandedWidth : 0;
        const slidesIntoBaseFromLeft = this.expandedDirection === 'left';
        const currentExpandedInnerRightOffset = target && currentExpandedWidth <= 0.5
            ? -expandedWidth
            : currentExpandedInnerRight;
        const targetExpandedInnerRightOffset = target ? 0 : -expandedWidth;
        const options = this.getAnimationOptions();

        const animations = [
            this.transparentDiv.animate(
                [{ width: currentSpacerWidth + 'px' }, { width: targetSpacerWidth + 'px' }],
                options
            ),
            this.panelsAnchor.animate(
                [{ left: currentAnchorLeft + 'px' }, { left: targetAnchorLeft + 'px' }],
                options
            ),
            this.expandedElement.animate(
                [{ width: currentExpandedWidth + 'px' }, { width: targetExpandedWidth + 'px' }],
                options
            ),
        ];

        if (slidesIntoBaseFromLeft) {
            animations.push(this.expandedInner.animate(
                [
                    { right: currentExpandedInnerRightOffset + 'px' },
                    { right: targetExpandedInnerRightOffset + 'px' },
                ],
                options
            ));
        }

        return new Promise((resolve) => {
            const transition = {
                animations,
                target,
                baseWidth,
                expandedWidth,
                resolve,
            };
            this._expandedTransition = transition;
            animations[2].onfinish = () => this.completeExpandedTransition(transition);
        });
    }

    /**
     * Opens the optional expanded panel when it can fit in the viewport.
     * @param {Event} [e] The event that requested expansion.
     * @returns {Promise<boolean>} True when expanded, otherwise false.
     */
    async expand(e) {
        if (this._expandedTarget) {
            return this._expandedOperationPromise ?? this._isExpanded;
        }
        if (!this.canExpand()) return false;

        this._expandedTarget = true;
        const operationVersion = ++this._expandedOperationVersion;
        const operation = (async () => {
            await Promise.resolve(this.beforeExpand(e));
            if (operationVersion !== this._expandedOperationVersion) return false;
            if (!this._expandedTarget || !this.canExpand()) {
                this._expandedTarget = false;
                return false;
            }

            event.dispatchCustomEvent(this, 'wje-sliding-container:beforeExpand');
            const baseWidth = this.getBaseWidthInPixels();
            const expandedWidth = this.resolveExpandedWidth();
            const completed = await this.animateExpandedTransition(true, baseWidth, expandedWidth);
            if (operationVersion !== this._expandedOperationVersion || !completed || !this._isExpanded) return false;

            await Promise.resolve(this.afterExpand(e));
            if (operationVersion !== this._expandedOperationVersion || !this._isExpanded) return false;
            event.dispatchCustomEvent(this, 'wje-sliding-container:expand');
            return true;
        })();

        this._expandedOperationPromise = operation;
        try {
            return await operation;
        } finally {
            if (this._expandedOperationPromise === operation) {
                this._expandedOperationPromise = null;
            }
        }
    }

    /**
     * Closes only the optional expanded panel and keeps its slotted DOM mounted.
     * @param {Event} [e] The event that requested collapse.
     * @returns {Promise<boolean>} True when the panel collapsed, otherwise false.
     */
    async collapse(e) {
        if (!this._expandedTarget && this._expandedOperationPromise) {
            return this._expandedOperationPromise;
        }
        if (!this._expandedTarget && !this._isExpanded) {
            return false;
        }

        this._expandedTarget = false;
        const operationVersion = ++this._expandedOperationVersion;
        const operation = (async () => {
            await Promise.resolve(this.beforeCollapse(e));
            if (operationVersion !== this._expandedOperationVersion || this._expandedTarget) return false;

            event.dispatchCustomEvent(this, 'wje-sliding-container:beforeCollapse');
            const baseWidth = this.getBaseWidthInPixels();
            const expandedWidth = this._expandedWidthPixels || this.resolveExpandedWidth();
            const completed = await this.animateExpandedTransition(false, baseWidth, expandedWidth);
            if (operationVersion !== this._expandedOperationVersion || !completed || this._isExpanded) return false;

            await Promise.resolve(this.afterCollapse(e));
            if (operationVersion !== this._expandedOperationVersion || this._isExpanded) return false;
            event.dispatchCustomEvent(this, 'wje-sliding-container:afterCollapse');
            return true;
        })();

        this._expandedOperationPromise = operation;
        try {
            return await operation;
        } finally {
            if (this._expandedOperationPromise === operation) {
                this._expandedOperationPromise = null;
            }
        }
    }

    /**
     * Toggles only the optional expanded panel.
     * @param {Event} [e] The event that requested the toggle.
     * @returns {Promise<boolean>} The result of expand or collapse.
     */
    toggleExpanded(e) {
        return this._expandedTarget || this._isExpanded ? this.collapse(e) : this.expand(e);
    }

    /**
     * Immediately resets expansion without detaching consumer-owned slot content.
     * @returns {void}
     */
    resetExpanded() {
        this._expandedOperationVersion++;
        this.cancelExpandedTransition();
        this._isExpanded = false;
        this._expandedTarget = false;
        this.classList.remove('expanded');

        if (this.panelsAnchor && this.expandedElement) {
            this.applyExpandedState(false);
        }
    }

    /**
     * Executes before the element is opened.
     */
    beforeOpen(e) {
        // Hook for extending behavior before the dialog opens
    }

    /**
     * Callback function called after the element is opened.
     */
    afterOpen(e) {
        // Hook for extending behavior after the dialog opens
    }

    /**
     * Executes before closing the element.
     */
    beforeClose(e) {
        // Hook for extending behavior before the dialog closes
    }

    /**
     * Callback function that is called after the container is closed.
     */
    afterClose(e) {
        // Hook for extending behavior after the dialog closes
    }

    /**
     * Returns animation options shared by side and bottom-sheet transitions.
     * @returns {object} Web Animation options.
     */
    getAnimationOptions() {
        return {
            delay: 0,
            endDelay: 0,
            fill: 'forwards',
            duration: +this.animationDuration,
            iterationStart: 0,
            iterations: 1,
            direction: 'normal',
            easing: this.animationEasing,
        };
    }

    /**
     * Settles the currently running base open/close transition before its DOM is
     * redrawn or disconnected. This prevents a delayed Web Animations cancel
     * callback from mutating a newer transition.
     */
    finishBaseTransitionImmediately() {
        this.baseTransition.finishImmediately();
    }

    /**
     * Applies a settled visual state to the side presentation.
     * @param {boolean} isOpen True when the panel is open.
     */
    setSideVisualState(isOpen) {
        if (this.transparentDiv) {
            this.transparentDiv.style.width = isOpen ? this.maxWidth : '0px';
        }
        if (this.nativeElement) {
            this.nativeElement.style.width = isOpen ? this.maxWidth : '0px';
            if (this.hasOpacity) {
                this.nativeElement.style.opacity = isOpen ? '1' : '0';
            }
        }
        this.backdropController.setOpen(isOpen);
    }

    /**
     * Animates the transition of elements with specified options, toggling the visibility and/or dimensions
     * of the associated elements based on their current state.
     *
     * This method handles both forward and reverse animations for two elements (`transparentDiv` and `nativeElement`)
     * with optional opacity changes. It ensures smooth transitioning by canceling any previous animations on the provided
     * elements before initiating a new animation sequence.
     * @returns {Promise<void>} A promise that resolves when the transition animation is completed.
     */
    doAnimateTransition() {
        const options = this.getAnimationOptions();

        if (this.usesBottomPresentation()) {
            return this.doAnimateBottomTransition(options);
        }

        const shouldOpen = !this._isOpen;

        if (this.animation && this.animation?.effect?.target !== this.transparentDiv) {
            this.animation.cancel();
            this.animation = null;
        }

        if (this.nativeAnimation && this.nativeAnimation?.effect?.target !== this.nativeElement) {
            this.nativeAnimation.cancel();
            this.nativeAnimation = null;
        }

        if (shouldOpen) {
            if (this.animation && this.nativeAnimation) {
                this.animation.reverse();
                this.nativeAnimation.reverse();
            } else {
                this.animation = this.transparentDiv.animate(
                    [
                        {
                            width: 0,
                        },
                        {
                            width: this.maxWidth,
                        },
                    ],
                    options
                );

                this.nativeAnimation = this.nativeElement.animate(
                    [
                        {
                            ...(this.hasOpacity ? { opacity: 0 } : {}),
                            width: 0,
                        },
                        {
                            ...(this.hasOpacity ? { opacity: 1 } : {}),
                            width: this.maxWidth,
                        },
                    ],
                    options
                );
            }
        } else {
            if (this.animation && this.nativeAnimation) {
                this.animation.reverse();
                this.nativeAnimation.reverse();
            } else {
                this.animation = this.transparentDiv.animate(
                    [
                        {
                            width: this.maxWidth,
                        },
                        {
                            width: 0,
                        },
                    ],
                    options
                );

                this.nativeAnimation = this.nativeElement.animate(
                    [
                        {
                            ...(this.hasOpacity ? { opacity: 1 } : {}),
                            width: this.maxWidth,
                        },
                        {
                            ...(this.hasOpacity ? { opacity: 0 } : {}),
                            width: 0,
                        },
                    ],
                    options
                );
            }
        }

        const backdropAnimation = this.backdropController.animate(shouldOpen, options);
        const animation = this.animation;
        const nativeAnimation = this.nativeAnimation;

        return this.baseTransition.run({
            primary: animation,
            animations: [animation, nativeAnimation],
            cleanup: () => {
                this.backdropController.cancelAnimation(backdropAnimation);
            },
            settle: () => {
                if (this.animation === animation) this.animation = null;
                if (this.nativeAnimation === nativeAnimation) this.nativeAnimation = null;
                this._isOpen = shouldOpen;
                this.classList.toggle('open', shouldOpen);
                this.setSideVisualState(shouldOpen);
            },
        });
    }

    /**
     * Animates the mobile bottom sheet and its backdrop.
     * @param {object} options Web Animation options.
     * @returns {Promise<void>}
     */
    doAnimateBottomTransition(options) {
        const shouldOpen = !this._isOpen;
        const closedSheet = {
            ...(this.hasOpacity ? { opacity: 0 } : {}),
            transform: 'translateY(100%)',
        };
        const openSheet = {
            ...(this.hasOpacity ? { opacity: 1 } : {}),
            transform: 'translateY(0)',
        };

        if (this.animation) {
            this.animation.cancel();
            this.animation = null;
        }

        if (this.nativeAnimation && this.nativeAnimation?.effect?.target !== this.nativeElement) {
            this.nativeAnimation.cancel();
            this.nativeAnimation = null;
        }

        if (shouldOpen) {
            if (this.nativeAnimation) {
                this.nativeAnimation.reverse();
            } else {
                this.nativeAnimation = this.nativeElement.animate([closedSheet, openSheet], options);
            }
        } else {
            if (this.nativeAnimation) {
                this.nativeAnimation.reverse();
            } else {
                this.nativeAnimation = this.nativeElement.animate([openSheet, closedSheet], options);
            }
        }

        const backdropAnimation = this.backdropController.animate(shouldOpen, options);
        const nativeAnimation = this.nativeAnimation;

        return this.baseTransition.run({
            primary: nativeAnimation,
            animations: [nativeAnimation],
            cleanup: () => {
                this.backdropController.cancelAnimation(backdropAnimation);
            },
            settle: () => {
                if (this.nativeAnimation === nativeAnimation) this.nativeAnimation = null;
                this._isOpen = shouldOpen;
                this.classList.toggle('open', shouldOpen);
                this.setBottomVisualState(shouldOpen);
            },
        });
    }

    /**
     * Opens the sliding container by performing necessary preparatory and transitional operations.
     * @param {Event} e The event that triggered the open operation.
     * @returns {Promise<void>} A promise that resolves when the open operation, including animations and subsequent handlers, is complete.
     */
    async open(e) {
        await Promise.resolve(this.beforeOpen(e)).then(async () => {
            if (!this._isOpen) {
                this.classList.add('open');

                event.dispatchCustomEvent(this, 'wje-sliding-container:beforeOpen');

                this.checkForVariant(this.presentationVariant);

                await this.doAnimateTransition();
                this.syncAria();

                await Promise.resolve(this.afterOpen(e)).then(() => {
                    event.dispatchCustomEvent(this, 'wje-sliding-container:open');
                });
            }
        });
    }

    /**
     * Closes the sliding container and performs associated operations such as animations and event dispatches.
     * @param {Event} e The event object associated with the close action.
     * @returns {Promise<void>} A promise that resolves when the closing operation, including animations and child element removal, is completed.
     */
    async close(e) {
        if (this.usesBottomPresentation()
            && e instanceof Event
            && (this.isSheetHandleEvent(e) || (this.backdropController.owns(e.target) && Date.now() < this._sheetIgnoreDismissUntil))) {
            return;
        }

        this._isClosing = true;
        try {
            if (this._isExpanded || this._expandedTarget) {
                this.resetExpanded();
            }

            await Promise.resolve(this.beforeClose(e)).then(async () => {
                if (this._isOpen) {
                    this.classList.remove('open');
                    this.setBaseInteractionState(false);

                    event.dispatchCustomEvent(this, 'wje-sliding-container:beforeClose');

                    await this.doAnimateTransition().catch(() => {
                        if (this.usesBottomPresentation()) {
                            this.setBottomClosedState();
                        }
                    });
                    this.syncAria();

                    await Promise.resolve(this.afterClose(e)).then(() => {
                        if (this.removeChildAfterClose) {
                            this.childNodes.forEach((child) => {
                                child.remove();
                            });
                        }

                        event.dispatchCustomEvent(this, 'wje-sliding-container:afterClose');
                    });
                }
            });
        } finally {
            this.resetExpanded();
            this._isClosing = false;
        }
    }

    /**
     * Forces the closed visual state for the mobile bottom sheet.
     */
    setBottomClosedState() {
        this._isOpen = false;
        this.classList.remove('open');
        this.setBaseInteractionState(false);
        this.setBottomVisualState(false);
    }

    /**
     * Toggles the state between open and closed.
     * @param {Event} e The event object triggering the toggle.
     * @returns {Promise<void>} A promise that resolves once the toggle operation (open or close) is complete.
     */
    async toggle(e) {
        if (this._isOpen) {
            await this.close(e);
        } else {
            await this.open(e);
        }
    }

    /**
     * Cleans up resources associated with the component by disconnecting
     * the resize observer and setting it to null.
     * @returns {void} Does not return a value.
     */
    componentCleanup() {
        this.finishBaseTransitionImmediately();
        this.resetExpanded();
        this.resetSideOverVariant();
        this.resetBottomPresentation();
        this.backdropController.cancelAnimation();
        this.endSheetDrag();
        this.backdropController.remove();
        this.unbindTrigger();
        this.unbindExpandedTrigger();
        this.stopResizeObservation();
    }
}
